import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, ExternalLink, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

export default function AssetPacksPage() {
  const [products, setProducts] = useState(null);

  useEffect(() => {
    api.get("/fab/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  return (
    <div data-testid="asset-packs-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title="Fab Asset Packs — UE5 Characters, Vehicles & Kits" description="Production-proven Unreal Engine asset packs by Specpixel Entertainment on Fab: AAA character packs, law-enforcement uniforms, historical kits and drivable vehicles." path="/asset-packs" keywords="Fab asset packs, UE5 assets, Unreal Engine marketplace, police character pack, Specpixel assets" />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">Specpixel Entertainment // Fab Store</p>
          <h1 data-testid="asset-packs-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
            Asset Packs
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            AAA-quality Unreal Engine assets built by our studio — characters, uniforms, vehicles and environment content.
            Every pack links directly to our Fab marketplace listing.
          </p>
          <a
            data-testid="fab-seller-link"
            href="https://www.fab.com/sellers/Specpixel%20Entertainment%20PTY%20LTD"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-nc-blue border border-nc-blue/40 hover:bg-nc-blue/10 px-4 py-2.5 transition-colors duration-300"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Synced from our Fab seller page <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full">
        {!products ? (
          <p data-testid="fab-loading" className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">Loading asset catalog...</p>
        ) : products.length === 0 ? (
          <p data-testid="fab-empty" className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase">No asset packs listed yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((p, i) => (
              <motion.a
                key={p.id}
                data-testid={`fab-product-${i + 1}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/10 bg-black/40 group hover:border-nc-blue/60 transition-colors duration-300 flex flex-col"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
              >
                <div className="relative aspect-square overflow-hidden bg-[#0a0a0a]">
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-10 h-10 text-zinc-700" />
                    </div>
                  )}
                  {p.discount && (
                    <span data-testid={`fab-product-${i + 1}-discount`} className="absolute top-3 left-3 bg-nc-orange text-white font-sans font-bold text-xs px-2 py-1">
                      {p.discount}
                    </span>
                  )}
                </div>
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <h3 className="font-sans font-semibold uppercase text-sm leading-snug text-zinc-100 group-hover:text-nc-blue transition-colors duration-300">
                    {p.name}
                  </h3>
                  <div className="mt-auto flex items-center justify-between">
                    <span data-testid={`fab-product-${i + 1}-price`} className="font-mono text-sm text-nc-green">{p.price}</span>
                    <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase flex items-center gap-1.5 group-hover:text-nc-blue transition-colors duration-300">
                      View on Fab <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
